import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

const OAUTH_SOURCE_KEY = 'metefy_oauth_source';
const REDIRECT_AFTER_LOGIN_KEY = 'metefy_redirect_after_login';

export default function AuthGoogleCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [message, setMessage] = useState('Completing Google sign in...');
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const completeAuth = async () => {
      try {
        const hash = window.location.hash || '';
        const hashParams = new URLSearchParams(
          hash.startsWith('#') ? hash.slice(1) : hash
        );

        const idToken = hashParams.get('id_token');

        console.log('[AuthGoogleCallback] Hash present:', !!hash);
        console.log('[AuthGoogleCallback] ID token present:', !!idToken);

        if (!idToken) {
          throw new Error('No ID token found in callback URL');
        }

        const { data, error } = await supabase.auth.signInWithIdToken({
          provider: 'google',
          token: idToken,
        });

        if (error) {
          console.error('[AuthGoogleCallback] signInWithIdToken error:', error);
          throw error;
        }

        if (!data.session || !data.user) {
          throw new Error('No session returned from Supabase');
        }

        setMessage('Sign in successful! Redirecting...');

        const source = window.localStorage.getItem(OAUTH_SOURCE_KEY);
        let redirectPath = '/profile';

        if (source === 'hero' || source === 'found-items-map') {
          redirectPath = '/found-items-map';
        }

        window.localStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
        window.localStorage.removeItem(OAUTH_SOURCE_KEY);

        setTimeout(() => navigate(redirectPath, { replace: true }), 2000);
      } catch (err: any) {
        console.error('[AuthGoogleCallback] Error:', err);
        setError(err?.message || 'Failed to complete Google sign in');
        setMessage('Authentication failed. Redirecting...');

        try {
          window.localStorage.removeItem(REDIRECT_AFTER_LOGIN_KEY);
          window.localStorage.removeItem(OAUTH_SOURCE_KEY);
        } catch {
          // Ignore storage errors
        }

        toast({
          title: 'Authentication Error',
          description: err?.message || 'Failed to complete sign in',
          variant: 'destructive',
        });

        setTimeout(() => navigate('/auth', { replace: true }), 3000);
      } finally {
        setIsProcessing(false);
      }
    };

    const timer = setTimeout(completeAuth, 500);
    return () => clearTimeout(timer);
  }, [navigate, searchParams, toast]);

  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="text-center max-w-md px-4">
        <h2 className="text-xl font-semibold mb-2">{message}</h2>
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 font-medium">Error:</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
        )}
        <p className="text-gray-600 mt-2">
          {isProcessing
            ? 'Please wait while we process your authentication...'
            : ''}
        </p>
      </div>
    </div>
  );
}

